import { useEffect, useMemo, useState } from "react";
import { motion, ease as mmEase } from "@/lib/motion";
import { Search, ArrowUpRight, ArrowDownLeft, Plus, CheckCircle2, Clock, XCircle, Globe2, WifiOff } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CURRENCY_BY_CODE } from "@/lib/constants";
import { formatAmount, relativeTime, initials } from "@/lib/format";

const ez = mmEase.expo;

const FILTERS = [
  { v: "all",     label: "All" },
  { v: "send",    label: "Sent" },
  { v: "receive", label: "Received" },
  { v: "topup",   label: "Top-ups" },
];

const STATUS_MAP = {
  completed:  { cls: "badge-emerald", icon: CheckCircle2, label: "Completed" },
  processing: { cls: "badge-gold",    icon: Clock,        label: "Processing" },
  pending:    { cls: "badge-gold",    icon: Clock,        label: "Pending" },
  failed:     { cls: "badge-red",     icon: XCircle,      label: "Failed" },
};

export default function Transactions() {
  const [txs,     setTxs]     = useState([]);
  const [filter,  setFilter]  = useState("all");
  const [q,       setQ]       = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    api.get("/transactions", { params: { limit: 200 } })
      .then(({ data }) => { setTxs(data); setFetchError(false); })
      .catch(() => {
        setFetchError(true);
        toast.error("Could not load transactions. Check your connection and try again.");
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = txs;
    if (filter !== "all") list = list.filter((t) => t.direction === filter);
    if (q) {
      const Q = q.toLowerCase();
      list = list.filter((t) =>
        (t.reference || "").toLowerCase().includes(Q) ||
        (t.beneficiary_snapshot?.name || "").toLowerCase().includes(Q) ||
        (t.from_currency || "").toLowerCase().includes(Q) ||
        (t.to_currency || "").toLowerCase().includes(Q)
      );
    }
    return list;
  }, [txs, filter, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const k = (t.created_at || "").slice(0, 10);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(t);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  return (
    <div className="space-y-5">

      {/* ── Header ──────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -12, filter: "blur(4px)" }}
        animate={{ opacity: 1, y: 0,   filter: "blur(0px)" }}
        transition={{ duration: 0.45, ease: ez }}
      >
        <p className="text-[10px] uppercase tracking-[0.22em] text-emerald font-bold mb-1">History</p>
        <h1 className="font-display text-[1.65rem] font-black text-navy tracking-tight">Transactions</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Every send, every receive, every top-up — searchable and receipt-ready.
        </p>
      </motion.div>

      {/* ── Controls ────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.36, ease: ez, delay: 0.06 }}
        className="flex flex-col sm:flex-row gap-3"
      >
        <label
          className="flex items-center gap-2 px-3.5 py-2.5 bg-white border border-border/70 rounded-2xl flex-1 cursor-text focus-within:border-emerald focus-within:shadow-[0_0_0_3px_rgba(16,185,129,0.1)] transition-all"
          htmlFor="tx-search-input"
        >
          <Search size={14} className="text-muted-foreground shrink-0" />
          <input
            id="tx-search-input"
            data-testid="tx-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search reference, recipient, currency…"
            className="bg-transparent outline-none text-sm flex-1 text-navy placeholder:text-muted-foreground"
          />
        </label>

        <div className="flex gap-1 p-1 bg-secondary/70 rounded-2xl self-start">
          {FILTERS.map((f) => (
            <button
              key={f.v}
              data-testid={`tx-filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                filter === f.v
                  ? "bg-emerald text-white shadow-[0_2px_12px_-2px_rgba(16,185,129,0.45)]"
                  : "text-muted-foreground hover:text-navy"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ── List ────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: ez, delay: 0.1 }}
        className="card-elevated overflow-hidden"
      >
        {loading && (
          <div className="space-y-0">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-5 py-4 border-b border-border/50 last:border-0">
                <div className="skeleton w-10 h-10 rounded-2xl shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="skeleton h-3.5 w-40 rounded" />
                  <div className="skeleton h-2.5 w-24 rounded" />
                </div>
                <div className="skeleton h-4 w-20 rounded" />
              </div>
            ))}
          </div>
        )}

        {!loading && fetchError && (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#EF4444]/[0.08] grid place-items-center mx-auto text-[#EF4444] mb-4">
              <WifiOff size={22} strokeWidth={1.6} />
            </div>
            <p className="font-bold text-navy text-sm">Could not load transactions</p>
            <p className="text-xs text-muted-foreground mt-1">Check your connection, then refresh the page.</p>
          </div>
        )}

        {!loading && !fetchError && grouped.length === 0 && (
          <div className="py-16 text-center">
            <div className="w-14 h-14 rounded-2xl bg-secondary/80 grid place-items-center mx-auto text-muted-foreground mb-4">
              <Globe2 size={22} strokeWidth={1.6} />
            </div>
            <p className="font-bold text-navy text-sm">No transactions match</p>
            <p className="text-xs text-muted-foreground mt-1">Try a different search or filter.</p>
          </div>
        )}

        {!loading && !fetchError && grouped.map(([day, list], gi) => (
          <div key={day}>
            <div className={`px-5 py-2.5 flex items-center gap-3 bg-secondary/30 ${gi > 0 ? "border-t border-border/40" : ""}`}>
              <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold">
                {new Date(day).toLocaleDateString("en-GB", {
                  weekday: "long", day: "2-digit", month: "long", year: "numeric",
                })}
              </p>
              <div className="flex-1 h-px bg-border/40" />
              <span className="text-[10px] font-bold text-muted-foreground bg-border/30 px-2 py-0.5 rounded-full">
                {list.length}
              </span>
            </div>
            <div className="divide-y divide-border/40">
              {list.map((t) => <TxRow key={t.id} tx={t} />)}
            </div>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function TxRow({ tx }) {
  const isSend  = tx.direction === "send";
  const isTopup = tx.direction === "topup";
  const fromC   = CURRENCY_BY_CODE[tx.from_currency];
  const toC     = CURRENCY_BY_CODE[tx.to_currency];
  const counter = tx.beneficiary_snapshot?.name || (isTopup ? "Wallet top-up" : "MoneyMeet");
  const { cls } = STATUS_MAP[tx.status] || STATUS_MAP.pending;

  const Icon = isTopup ? Plus : isSend ? ArrowUpRight : ArrowDownLeft;
  const iconBg = isTopup
    ? "bg-[#F59E0B]/[0.10] text-[#F59E0B]"
    : isSend
    ? "bg-coral/[0.09] text-coral"
    : "bg-emerald/10 text-emerald";

  return (
    <Link
      to={`/app/transactions/${tx.id}`}
      data-testid={`tx-row-${tx.id}`}
      className="flex items-center gap-3 px-5 py-3.5 hover:bg-secondary/40 transition-colors group"
    >
      <div className={`w-10 h-10 rounded-2xl grid place-items-center shrink-0 text-sm font-bold transition-colors ${iconBg}`}>
        {isSend && !isTopup
          ? <span className="text-[11px] font-black">{initials(counter)}</span>
          : <Icon size={15} strokeWidth={2.2} />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-navy text-[13px]">
            {isTopup ? "Wallet top-up" : isSend ? `To ${counter}` : `From ${counter}`}
          </span>
          <span className={`${cls} text-[9px]`}>{STATUS_MAP[tx.status]?.label || tx.status}</span>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
          <span className="font-mono text-[10px]">{tx.reference}</span>
          <span className="text-border">·</span>
          <span>{relativeTime(tx.created_at)}</span>
        </div>
      </div>

      <div className="text-right shrink-0">
        <p className={`font-bold text-[13px] num-tabular ${isSend ? "text-navy" : "text-emerald"}`}>
          {isSend ? "−" : "+"}{formatAmount(tx.send_amount, tx.from_currency)}
        </p>
        {tx.from_currency !== tx.to_currency && (
          <p className="text-[10px] text-muted-foreground num-tabular">
            {fromC?.flag}→{toC?.flag} {formatAmount(tx.receive_amount, tx.to_currency)}
          </p>
        )}
      </div>
    </Link>
  );
}
